import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { catchError, lastValueFrom, of, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import {
  ParentCourseDetail,
  ParentCourseEnrollment,
} from '../models/parent-learning.types';

export const PARENT_LEARNING_QUERY_KEYS = {
  detail: (courseId: string) => ['parentCourseDetail', courseId] as const,
  enrollment: (courseId: string) =>
    ['parentCourseEnrollment', courseId] as const,
  task: (taskId: string) => ['parentLearningTask', taskId] as const,
  quiz: (taskId: string) => ['parentLearningQuiz', taskId] as const,
};

@Injectable({ providedIn: 'root' })
export class ParentLearningService {
  #http = inject(HttpClient);
  #queryClient = inject(QueryClient);

  fetchCourseDetail(courseId: string): Promise<ParentCourseDetail> {
    return lastValueFrom(
      this.#http
        .get<ParentCourseDetail>(
          `${environment.baseUrl}/courses/${courseId}/detail`,
        )
        .pipe(take(1)),
    );
  }

  async fetchEnrollment(courseId: string): Promise<ParentCourseEnrollment> {
    const res = await lastValueFrom(
      this.#http
        .get<ChallengeGen.CourseRes>(`${environment.baseUrl}/courses/available`)
        .pipe(take(1)),
    );
    const course =
      res.data?.find((c) => c.id === courseId) ?? null;
    return {
      course,
      courseJoined: course?.courseJoined === true,
      progress: course?.completionPercent ?? 0,
      courseCompleted: course?.courseCompleted === true,
    };
  }

  fetchTaskPlain(taskId: string): Promise<ChallengeGen.Task> {
    return lastValueFrom(
      this.#http
        .get<ChallengeGen.Task>(
          `${environment.baseUrl}/tasks/${taskId}/plain`,
        )
        .pipe(take(1)),
    );
  }

  /** Plain tasks omit quiz relations — load quiz separately when needed. */
  fetchQuizByTaskId(taskId: string): Promise<ChallengeGen.Quiz | null> {
    return lastValueFrom(
      this.#http
        .get<ChallengeGen.Quiz>(
          `${environment.baseUrl}/tasks/${taskId}/quiz`,
        )
        .pipe(
          take(1),
          catchError((err: HttpErrorResponse) => {
            if (err.status === 404) {
              return of(null);
            }
            throw err;
          }),
        ),
    );
  }

  joinCourse(courseId: string): Promise<ChallengeGen.Course> {
    return lastValueFrom(
      this.#http
        .post<ChallengeGen.Course>(
          `${environment.baseUrl}/courses/${courseId}/join`,
          {},
        )
        .pipe(take(1)),
    );
  }

  leaveCourse(courseId: string): Promise<ChallengeGen.Course> {
    return lastValueFrom(
      this.#http
        .post<ChallengeGen.Course>(
          `${environment.baseUrl}/courses/${courseId}/leave`,
          {},
        )
        .pipe(take(1)),
    );
  }

  invalidateCourse(courseId: string): void {
    this.#queryClient.invalidateQueries({
      queryKey: PARENT_LEARNING_QUERY_KEYS.detail(courseId),
    });
    this.#queryClient.invalidateQueries({
      queryKey: PARENT_LEARNING_QUERY_KEYS.enrollment(courseId),
    });
    this.#queryClient.invalidateQueries({
      queryKey: ['courseAvailableLight', 'parent'],
    });
    this.#queryClient.invalidateQueries({
      queryKey: ['courseAvailableSummary', 'parent'],
    });
  }

  invalidateTask(taskId: string): void {
    this.#queryClient.invalidateQueries({
      queryKey: PARENT_LEARNING_QUERY_KEYS.task(taskId),
    });
    this.#queryClient.invalidateQueries({
      queryKey: PARENT_LEARNING_QUERY_KEYS.quiz(taskId),
    });
  }

  prefetchLesson(taskId: string): void {
    void this.#queryClient.prefetchQuery({
      queryKey: PARENT_LEARNING_QUERY_KEYS.task(taskId),
      queryFn: () => this.fetchTaskPlain(taskId),
      staleTime: 60_000,
    });
    void this.#queryClient.prefetchQuery({
      queryKey: PARENT_LEARNING_QUERY_KEYS.quiz(taskId),
      queryFn: () => this.fetchQuizByTaskId(taskId),
      staleTime: 60_000,
    });
  }

  rateCourse(courseId: string, rating: number): Promise<ChallengeGen.Rating> {
    return lastValueFrom(
      this.#http
        .post<ChallengeGen.Rating>(`${environment.baseUrl}/courses/rate`, {
          courseId,
          rating,
        })
        .pipe(take(1)),
    );
  }
}
